import LegalPage from "@/components/LegalPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import SupportRequestForm from "@/components/SupportRequestForm";

const DMCAPage = () => (
  <LegalPage
    title="DMCA / Copyright Policy"
    updated="June 18, 2026"
    intro={
      <p>
        Vecurate respects the intellectual property rights of others and expects users to do the same.
        This policy explains how copyright owners can submit a takedown notice under the Digital
        Millennium Copyright Act (DMCA), and how affected users can submit a counter-notification.
      </p>
    }
  >
    <h2>1. Reporting Alleged Copyright Infringement</h2>
    <p>
      If you believe that material posted on Vecurate infringes your copyright, please send a written
      notice that includes the information required by 17 U.S.C. § 512(c)(3). To be effective, your
      notice must include all of the following:
    </p>
    <ol>
      <li>A physical or electronic signature of the copyright owner or a person authorized to act on their behalf;</li>
      <li>Identification of the copyrighted work claimed to have been infringed (or a representative list if multiple works on the Service are covered);</li>
      <li>Identification of the material that is claimed to be infringing or to be the subject of infringing activity, and information reasonably sufficient to permit us to locate it (for example, the URL of the specific review or page);</li>
      <li>Information reasonably sufficient to permit us to contact you, including your name, mailing address, telephone number, and email address;</li>
      <li>A statement that you have a good-faith belief that use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law;</li>
      <li>A statement, made under penalty of perjury, that the information in the notice is accurate and that you are the copyright owner or are authorized to act on the copyright owner's behalf.</li>
    </ol>

    <h2>2. Where to Send Notices</h2>
    <p>Submit DMCA notices to Vecurate's designated agent by email:</p>
    <div className="callout">
      <p className="!mb-1"><strong>Vecurate — DMCA Designated Agent</strong></p>
      <p className="!mb-0">Email: <a href="mailto:support@vecurate.net">support@vecurate.net</a></p>
    </div>
    <p>
      Email is the only accepted method for submitting DMCA notices. Notices that do not substantially
      comply with the requirements above may not be effective.
    </p>

    <Dialog>
      <DialogTrigger asChild>
        <Button className="rounded-lg not-prose mt-2">Submit a DMCA notice</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Submit a DMCA notice</DialogTitle>
          <DialogDescription>
            Include the URL of the infringing material and confirm the statements required by 17 U.S.C. § 512(c)(3) in your message.
          </DialogDescription>
        </DialogHeader>
        <SupportRequestForm
          requestType="dmca"
          defaultSubject="DMCA takedown notice"
          submitLabel="Submit DMCA notice"
        />
      </DialogContent>
    </Dialog>


    <h2>3. What Happens After We Receive a Valid Notice</h2>
    <ul>
      <li>We will promptly review the notice and, where appropriate, remove or disable access to the material identified;</li>
      <li>We will take reasonable steps to notify the user who posted the material that it has been removed or disabled because of a copyright complaint;</li>
      <li>We may forward a copy of the notice (including your contact information) to the user who posted the material;</li>
      <li>We will keep a record of the notice and any actions taken.</li>
    </ul>

    <h2>4. Counter-Notification</h2>
    <p>
      If you believe your material was removed or disabled by mistake or misidentification, you may
      submit a counter-notification under 17 U.S.C. § 512(g). Your counter-notification must include:
    </p>
    <ol>
      <li>Your physical or electronic signature;</li>
      <li>Identification of the material that has been removed or to which access has been disabled, and the location at which the material appeared before it was removed or access was disabled;</li>
      <li>A statement under penalty of perjury that you have a good-faith belief that the material was removed or disabled as a result of mistake or misidentification of the material;</li>
      <li>Your name, mailing address, telephone number, and email address; and</li>
      <li>A statement that you consent to the jurisdiction of the U.S. Federal District Court for the judicial district in which your address is located (or, if your address is outside the United States, for any judicial district in which Vecurate may be found), and that you will accept service of process from the person who provided the original DMCA notice or from an agent of that person.</li>
    </ol>
    <p>
      Send counter-notifications to <a href="mailto:support@vecurate.net">support@vecurate.net</a> using the
      contact details above.
    </p>

    <h2>5. What Happens After a Counter-Notification</h2>
    <p>
      If we receive a valid counter-notification, we will forward it to the original complaining party.
      Unless that party notifies us within 10 to 14 business days that they have filed a court action
      seeking to restrain the allegedly infringing activity, we may restore the removed material.
    </p>

    <h2>6. Repeat Infringers</h2>
    <p>
      Vecurate maintains a policy of terminating, in appropriate circumstances, the accounts of users
      who are determined to be repeat infringers of copyright.
    </p>

    <h2>7. False Claims</h2>
    <p>
      Under 17 U.S.C. § 512(f), any person who knowingly materially misrepresents that material is
      infringing, or that material was removed or disabled by mistake or misidentification, may be liable
      for damages. Please consult with an attorney before submitting a notice or counter-notification if
      you are unsure whether the material in question is infringing.
    </p>

    <div className="callout">
      <strong>Note:</strong> This policy summarizes Vecurate's DMCA procedures and is not legal advice.
      Final contact information and procedures will be confirmed by qualified legal counsel before
      public launch.
    </div>
  </LegalPage>
);

export default DMCAPage;
